import React, { useState } from 'react';
import { QueryListResponse, SelectOption } from 'global';
import { logError } from 'utils/logger';
import { bpfAPI, BPFAPINameSpase } from 'modules/stages';
import { CircularProgress, Stack } from '@mui/material';
import { useNotifications } from 'modules/notification';
import { useLazyQuery, useQuery } from '@apollo/client';
import { MoveCardEventParams } from 'components/shared/Board/types';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { dealPipeLineApi, initialVODealPipeLineFiltersValue, VOFilters } from 'modules/vendorOpportunity';

import { useVO } from '../../hooks/useVO';
import { DealPipeLine } from './DealPipeLine';
import { BoardDeal, DealPipelineResponse, DealPipelineVariables } from '../types';
import { calcAmountOfActiveFilters, generateBoardColumns, groupVOByStage } from '../lib';

export const DealPipelineCard = ({ accountId }: { accountId: string }) => {
  const { handleUpdateCurrentStage } = useVO();
  const { showNotification } = useNotifications();

  const [selectedDepartment, setDepartment] = useState<string | null>(null);
  const [filters, setFilters] = useState<Partial<VOFilters>>(initialVODealPipeLineFiltersValue);

  const mappedFilters: DealPipelineVariables['input']['filters'] = {
    isIncludeClosedDeals: filters.CLOSED_DEALS!.isIncludeClosedDeals,
    created: {
      from: filters.CREATE_DATE_RANGE!.from.toISOString(),
      to: filters.CREATE_DATE_RANGE!.to.toISOString(),
    },
  };

  const [onFetchDealPipelineData, { data: listResponse, loading: isDealInfoLoading }] = useLazyQuery<
    DealPipelineResponse,
    DealPipelineVariables
  >(dealPipeLineApi.fetchListWithSummary, {
    notifyOnNetworkStatusChange: true,
  });

  const [onFetchStagesByDepartment, { loading: isStagesLoading, data: stagesResponse }] = useLazyQuery(
    bpfAPI.fetchStagesByDepartment
  );

  const {
    data: departments,
    error: departmentsError,
    loading: isDepartmentsLoading,
  } = useQuery<BPFAPINameSpase<QueryListResponse<SelectOption>>>(bpfAPI.department.fetchDepartmentsOptions, {
    onCompleted({ bpf }) {
      if (!bpf?.result?.list?.length) {
        showNotification('Flows empty!', { type: 'info' });
        return;
      }

      const defaultDepartmentId = bpf.result?.list[0].value;

      setDepartment(defaultDepartmentId);

      Promise.all([
        onFetchDealPipelineData({
          variables: { input: { accountId, departmentId: defaultDepartmentId, filters: mappedFilters } },
        }),
        onFetchStagesByDepartment({ variables: { input: { departmentId: defaultDepartmentId, accountId } } }),
      ]).catch(logError);
    },
  });

  const groupedVOs = groupVOByStage(listResponse?.dealPipeline?.list ?? [], filters);
  const columns = generateBoardColumns(stagesResponse?.bpf?.stages ?? [], filters, groupedVOs);

  const handleMoveCard = async ({ toColumnId, cardEntity }: MoveCardEventParams & { cardEntity: BoardDeal }) => {
    await handleUpdateCurrentStage({ voId: cardEntity.id, stageId: toColumnId, flowId: cardEntity.flowId });
  };

  return (
    <RenderWithFallback
      error={departmentsError}
      isLoading={isDepartmentsLoading}
      fallBack={
        <Stack height="100%" width="100%" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Stack>
      }
    >
      <DealPipeLine
        columns={columns}
        onMoveCard={handleMoveCard}
        isStagesLoading={isStagesLoading}
        isDealInfoLoading={isDealInfoLoading}
        isDepartmentsLoading={isDepartmentsLoading}
        departments={departments?.bpf?.result?.list ?? []}
        summary={listResponse?.dealPipeline?.summary ?? null}
        amountOfActiveFilters={calcAmountOfActiveFilters(filters)}
        onFiltersChanges={async nextFilters => {
          setFilters(nextFilters);

          if (selectedDepartment) {
            await onFetchDealPipelineData({
              variables: { input: { accountId, departmentId: selectedDepartment, filters: mappedFilters } },
            });
          }
        }}
        onDepartmentChange={async departmentId => {
          setDepartment(departmentId);

          await onFetchDealPipelineData({
            variables: { input: { accountId, departmentId, filters: mappedFilters } },
          });
          await onFetchStagesByDepartment({ variables: { input: { departmentId, accountId } } });
        }}
      />
    </RenderWithFallback>
  );
};
